// SimulationCanvas — full SVG timeline with fixed lane labels.
// Zoom/pan uses SVG viewBox for crisp vector rendering at all scales.

import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store/index.ts';
import { selectActiveEventIndex, selectActualPath } from '../../store/slices/simulationSlice.ts';
import { setPan } from '../../store/slices/canvasSlice.ts';
import { resolveLaneColor, NDD_PALETTE, INK_DARK } from '../../services/styleResolver.ts';
import NodeBox from './NodeBox.tsx';
import ConnectionLayer from './ConnectionLayer.tsx';
import HopLayer from './HopLayer.tsx';
import NoteBand from './NoteBand.tsx';
import NodeAssertionTooltip from './NodeAssertionTooltip.tsx';
import TerminusBadge from './TerminusBadge.tsx';
import StartMarker from './StartMarker.tsx';
import { selectTerminus } from './runStory.ts';
import { useCanvasTransform } from './useCanvasTransform.ts';
import { useCanvasLayout } from './useCanvasLayout.ts';
import { LANE_LABEL_W, FRAME_W, HEADER_H } from './layoutEngine.ts';

export default function SimulationCanvas() {
  const dispatch = useDispatch();
  const activeEventIndex = useSelector(selectActiveEventIndex);
  const actualPath = useSelector(selectActualPath);

  const { containerRef, viewBox, containerSize, scale, pan, handlers } = useCanvasTransform();

  const layout = useCanvasLayout();

  // Map: scenario node ID → topology node ID
  const scenarioToTopoMap = useSelector(
    (state: RootState) => state.topology.scenarioToTopologyNodeMap,
  );

  // Terminus — where and why the run's path ended. Null until the run
  // reaches a terminal status, so nothing shifts during playback.
  const terminus = useSelector(selectTerminus);
  const terminusPos = useMemo(() => {
    if (!terminus) return null;
    const topoNodeId = scenarioToTopoMap[terminus.nodeId] ?? terminus.nodeId;
    return layout.nodePositions[topoNodeId] ?? layout.nodePositions[terminus.nodeId] ?? null;
  }, [terminus, scenarioToTopoMap, layout.nodePositions]);

  // Start — the first node of the walked path, marked from the first step on.
  const startPos = useMemo(() => {
    const first = actualPath[0];
    if (!first) return null;
    const topoNodeId = scenarioToTopoMap[first] ?? first;
    return layout.nodePositions[topoNodeId] ?? layout.nodePositions[first] ?? null;
  }, [actualPath, scenarioToTopoMap, layout.nodePositions]);

  // Lanes a note speaks for (Moment ADR-040 D3)
  const noteLaneIds = useMemo(
    () => new Set(layout.notePositions.map((n) => n.laneId)),
    [layout.notePositions],
  );

  // Index into layout.visibleFrames that contains the active event's node
  const activeFrameIndex = useMemo(() => {
    if (activeEventIndex === null) return -1;
    const activeNodeId = actualPath[activeEventIndex];
    if (!activeNodeId) return -1;
    // Resolve scenario ID to topology ID for frame lookup
    const topoNodeId = scenarioToTopoMap[activeNodeId] ?? activeNodeId;
    return layout.visibleFrames.findIndex((f) =>
      f.nodes.some((n) => n.id === topoNodeId || n.id === activeNodeId),
    );
  }, [activeEventIndex, actualPath, scenarioToTopoMap, layout.visibleFrames]);

  // Auto-pan to center the active frame column in the viewport
  useEffect(() => {
    if (activeFrameIndex < 0) return;
    const el = containerRef.current;
    if (!el) return;

    const frameCenterX = activeFrameIndex * FRAME_W + FRAME_W / 2;
    const viewportWidth = el.clientWidth;
    const targetPanX = viewportWidth / 2 - frameCenterX * scale;

    dispatch(setPan({ x: targetPanX, y: pan.y }));
  }, [activeFrameIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="simulation-canvas">
      {/* ── Fixed left: Lane Labels (HTML) ── */}
      <div
        className="lane-labels"
        style={{
          width: `${LANE_LABEL_W}px`,
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            transform: `translateY(${pan.y}px)`,
            transformOrigin: '0 0',
          }}
        >
          <div className="lane-labels-header" style={{ height: `${HEADER_H * scale}px` }}>
            <span className="lane-labels-title">Swim Lanes</span>
          </div>
          {layout.visibleLanes.map((lane, i) => {
            const h = layout.laneHeights[lane.id] * scale;
            const isContext =
              lane.isBranch !== undefined ? !lane.isBranch : lane.group === 'context';
            const laneColor = resolveLaneColor(lane);
            const prevLane = i > 0 ? layout.visibleLanes[i - 1] : null;
            const isCrossBoundary =
              isContext &&
              prevLane?.contextId != null &&
              lane.contextId != null &&
              prevLane.contextId !== lane.contextId;
            return (
              <div
                key={lane.id}
                className={`lane-label-row ${isCrossBoundary ? 'lane-label-boundary' : ''}`}
                style={{
                  height: `${h}px`,
                  borderBottom: isContext
                    ? '1px solid var(--canvas-subtle-stroke)'
                    : `1px solid ${laneColor}15`,
                  background: !isContext
                    ? `${laneColor}06`
                    : i % 2 === 0
                      ? 'transparent'
                      : 'var(--canvas-lane-alt)',
                }}
              >
                <div
                  className="lane-label-indicator"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: isContext ? '2px' : '50%',
                    background: laneColor,
                    flexShrink: 0,
                    boxShadow: !isContext ? `0 0 6px ${laneColor}40` : 'none',
                  }}
                />
                <div className="lane-label-text">
                  <div
                    className="lane-label-name"
                    data-note-lane={noteLaneIds.has(lane.id) ? 'true' : undefined}
                    style={
                      noteLaneIds.has(lane.id) && /^external$/i.test(lane.classification)
                        ? {
                            // A note on an [External] lane: the label takes the External system swatch
                            color: INK_DARK,
                            background: NDD_PALETTE.external.fill,
                            borderRadius: 3,
                            padding: '0 4px',
                            display: 'inline-block',
                          }
                        : { color: laneColor }
                    }
                  >
                    {lane.label}
                  </div>
                  <div className="lane-label-meta">
                    <span
                      className={`lane-classification-badge lane-classification-${lane.classification.toLowerCase()}`}
                    >
                      {lane.classification}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Zoomable/pannable timeline area ── */}
      <div ref={containerRef} className="timeline-scroll" {...handlers}>
        <svg width="100%" height="100%" viewBox={viewBox} style={{ display: 'block' }}>
          {/* Frame column headers */}
          {layout.visibleFrames.map((frame, fi) => {
            const isActiveFrame = fi === activeFrameIndex;
            // Strip redundant "moment-N-" prefix from frame IDs for display
            const frameLabel = frame.id.replace(/^moment-\d+-/i, '').toUpperCase();
            const clipId = `fh-clip-${fi}`;
            const maxTextW = FRAME_W - 20; // 10px padding each side
            return (
              <g key={`fh-${frame.id}`}>
                {/* Full-height column highlight behind active frame */}
                {isActiveFrame && (
                  <rect
                    x={fi * FRAME_W}
                    y={HEADER_H}
                    width={FRAME_W}
                    height={layout.totalHeight - HEADER_H}
                    fill="var(--canvas-active-col)"
                  />
                )}
                <rect
                  x={fi * FRAME_W}
                  y={0}
                  width={FRAME_W}
                  height={HEADER_H}
                  fill={
                    isActiveFrame
                      ? 'var(--canvas-active-bg)'
                      : frame.isBranch
                        ? 'rgba(253,224,71,0.03)'
                        : 'var(--canvas-frame-bg)'
                  }
                  stroke={
                    isActiveFrame ? 'var(--canvas-active-border)' : 'var(--canvas-subtle-stroke)'
                  }
                  strokeWidth={1}
                />
                <clipPath id={clipId}>
                  <rect x={fi * FRAME_W + 10} y={0} width={maxTextW} height={HEADER_H} />
                </clipPath>
                <text
                  x={fi * FRAME_W + 10}
                  y={18}
                  fill={
                    isActiveFrame
                      ? 'var(--indigo)'
                      : frame.isBranch
                        ? 'var(--amber)'
                        : 'var(--text-dim)'
                  }
                  fontSize="9"
                  fontFamily="'JetBrains Mono', monospace"
                  fontWeight={600}
                  clipPath={`url(#${clipId})`}
                >
                  {frame.isBranch ? '⬦ ' : ''}
                  {frameLabel}
                </text>
                <text
                  x={fi * FRAME_W + 10}
                  y={34}
                  fill={isActiveFrame ? 'var(--canvas-active-text)' : 'var(--text)'}
                  fontSize="10"
                  fontFamily="'IBM Plex Sans', sans-serif"
                  fontWeight={500}
                  clipPath={`url(#${clipId})`}
                >
                  {frame.moment}
                </text>
                {/* Column separator line */}
                <line
                  x1={fi * FRAME_W}
                  y1={HEADER_H}
                  x2={fi * FRAME_W}
                  y2={layout.totalHeight}
                  stroke={
                    isActiveFrame ? 'var(--canvas-active-sep)' : 'var(--canvas-subtle-stroke)'
                  }
                  strokeWidth={1}
                />
              </g>
            );
          })}

          {/* Lane background rects */}
          {layout.visibleLanes.map((lane, li) => {
            const y = layout.laneYStart[lane.id];
            const h = layout.laneHeights[lane.id];
            const isCtx = lane.isBranch !== undefined ? !lane.isBranch : lane.group === 'context';
            const svgLaneColor = resolveLaneColor(lane);
            return (
              <rect
                key={`lb-${lane.id}`}
                x={0}
                y={y}
                width={layout.totalWidth}
                height={h}
                fill={
                  !isCtx
                    ? svgLaneColor
                    : li % 2 === 0
                      ? 'transparent'
                      : 'var(--canvas-lane-alt-fill)'
                }
                fillOpacity={!isCtx ? 0.02 : 1}
                stroke={!isCtx ? svgLaneColor : 'var(--canvas-subtle-stroke)'}
                strokeOpacity={!isCtx ? 0.08 : 1}
                strokeWidth={1}
              />
            );
          })}

          {/* Cross-context boundary separators */}
          {layout.visibleLanes.map((lane, li) => {
            if (li === 0) return null;
            const prevLane = layout.visibleLanes[li - 1];
            // Only draw a strong separator when adjacent lanes have different contextIds
            if (!lane.contextId || !prevLane.contextId) return null;
            if (lane.contextId === prevLane.contextId) return null;
            const y = layout.laneYStart[lane.id];
            return (
              <line
                key={`ctx-sep-${lane.id}`}
                x1={0}
                y1={y}
                x2={layout.totalWidth}
                y2={y}
                stroke="rgba(148,163,184,0.2)"
                strokeWidth={1.5}
                strokeDasharray="6,4"
              />
            );
          })}

          {/* Connections (rendered below nodes) */}
          <ConnectionLayer nodePositions={layout.nodePositions} />

          {/* Nodes */}
          {layout.visibleFrames.flatMap((frame) =>
            frame.nodes
              .filter((n) => layout.nodePositions[n.id])
              .map((node) => (
                <NodeBox key={node.id} node={node} pos={layout.nodePositions[node.id]} />
              )),
          )}

          {/* Notes of the moments (Moment ADR-040 D3) */}
          {layout.notePositions.map((note) => (
            <NoteBand key={note.key} note={note} />
          ))}

          {/* Same-moment hops (Moment ADR-040 D4) — above the tiles they join */}
          <HopLayer nodePositions={layout.nodePositions} />

          {startPos && <StartMarker pos={startPos} />}

          {/* Terminus end-cap + badge — rendered above all nodes */}
          {terminus && terminusPos && <TerminusBadge terminus={terminus} pos={terminusPos} />}
        </svg>
      </div>

      {/* Assertion tooltip overlay — rendered outside timeline-scroll
          so it's not blocked by the full-size SVG or pan handlers */}
      <NodeAssertionTooltip
        nodePositions={layout.nodePositions}
        scale={scale}
        pan={pan}
        containerSize={containerSize}
        offsetLeft={LANE_LABEL_W}
      />
    </div>
  );
}
